/**
 * The special characters the picker offers, in the categories a word processor
 * groups them by.
 *
 * Hand-listed on purpose, unlike the emoji: the full Unicode tables run to tens
 * of thousands of code points, almost none of which anyone types into a notice
 * or a contract. These are the ones that come up — currency, maths, arrows,
 * punctuation and the accented Latin and Greek letters — and they go in as real
 * TEXT, so they stay editable and searchable afterwards.
 *
 * `name` is what the search matches and what the button reads out.
 */
export const SYMBOL_GROUPS = [
    {
        id: 'currency',
        icon: '৳',
        chars: [
            { char: '৳', name: 'taka bangladeshi currency' },
            { char: '₹', name: 'rupee indian currency' },
            { char: '$', name: 'dollar currency' },
            { char: '€', name: 'euro currency' },
            { char: '£', name: 'pound sterling currency' },
            { char: '¥', name: 'yen yuan currency' },
            { char: '₩', name: 'won currency' },
            { char: '₽', name: 'ruble currency' },
            { char: '﷼', name: 'riyal currency' },
            { char: '¢', name: 'cent currency' },
            { char: '¤', name: 'generic currency sign' },
        ],
    },
    {
        id: 'maths',
        icon: '±',
        chars: [
            { char: '±', name: 'plus minus' },
            { char: '×', name: 'multiply times' },
            { char: '÷', name: 'divide' },
            { char: '≠', name: 'not equal' },
            { char: '≈', name: 'approximately equal' },
            { char: '≤', name: 'less than or equal' },
            { char: '≥', name: 'greater than or equal' },
            { char: '<', name: 'less than' },
            { char: '>', name: 'greater than' },
            { char: '√', name: 'square root radical' },
            { char: '∑', name: 'sum sigma total' },
            { char: '∏', name: 'product pi' },
            { char: '∫', name: 'integral' },
            { char: '∞', name: 'infinity' },
            { char: '∂', name: 'partial derivative' },
            { char: '∆', name: 'delta change increment' },
            { char: '°', name: 'degree' },
            { char: '%', name: 'percent' },
            { char: '‰', name: 'per mille' },
            { char: '¼', name: 'one quarter fraction' },
            { char: '½', name: 'one half fraction' },
            { char: '¾', name: 'three quarters fraction' },
            { char: '⅓', name: 'one third fraction' },
            { char: 'π', name: 'pi constant' },
            { char: '∈', name: 'element of member' },
            { char: '∀', name: 'for all' },
            { char: '∃', name: 'there exists' },
            { char: '⊂', name: 'subset' },
            { char: '∪', name: 'union' },
            { char: '∩', name: 'intersection' },
        ],
    },
    {
        id: 'arrows',
        icon: '→',
        chars: [
            { char: '→', name: 'arrow right' },
            { char: '←', name: 'arrow left' },
            { char: '↑', name: 'arrow up' },
            { char: '↓', name: 'arrow down' },
            { char: '↔', name: 'arrow left right' },
            { char: '↕', name: 'arrow up down' },
            { char: '⇒', name: 'double arrow right implies' },
            { char: '⇐', name: 'double arrow left' },
            { char: '⇔', name: 'double arrow both if and only if' },
            { char: '↗', name: 'arrow up right' },
            { char: '↘', name: 'arrow down right' },
            { char: '↩', name: 'arrow return hook' },
            { char: '⟶', name: 'long arrow right' },
            { char: '➤', name: 'pointer arrow bullet' },
        ],
    },
    {
        id: 'punctuation',
        icon: '§',
        chars: [
            { char: '§', name: 'section clause' },
            { char: '¶', name: 'paragraph pilcrow' },
            { char: '†', name: 'dagger footnote' },
            { char: '‡', name: 'double dagger' },
            { char: '•', name: 'bullet dot' },
            { char: '·', name: 'middle dot' },
            { char: '…', name: 'ellipsis three dots' },
            { char: '–', name: 'en dash' },
            { char: '—', name: 'em dash' },
            { char: '‑', name: 'non breaking hyphen' },
            { char: '«', name: 'left guillemet quote' },
            { char: '»', name: 'right guillemet quote' },
            { char: '“', name: 'left double quote' },
            { char: '”', name: 'right double quote' },
            { char: '‘', name: 'left single quote' },
            { char: '’', name: 'right single quote apostrophe' },
            { char: '©', name: 'copyright' },
            { char: '®', name: 'registered trademark' },
            { char: '™', name: 'trademark' },
            { char: '№', name: 'numero number sign' },
            { char: '°', name: 'degree sign' },
            { char: '‽', name: 'interrobang' },
        ],
    },
    {
        id: 'latin',
        icon: 'é',
        chars: [
            { char: 'á', name: 'a acute' }, { char: 'à', name: 'a grave' },
            { char: 'â', name: 'a circumflex' }, { char: 'ä', name: 'a umlaut diaeresis' },
            { char: 'å', name: 'a ring' }, { char: 'æ', name: 'ae ligature' },
            { char: 'ç', name: 'c cedilla' },
            { char: 'é', name: 'e acute' }, { char: 'è', name: 'e grave' },
            { char: 'ê', name: 'e circumflex' }, { char: 'ë', name: 'e umlaut' },
            { char: 'í', name: 'i acute' }, { char: 'î', name: 'i circumflex' },
            { char: 'ï', name: 'i umlaut' }, { char: 'ñ', name: 'n tilde' },
            { char: 'ó', name: 'o acute' }, { char: 'ô', name: 'o circumflex' },
            { char: 'ö', name: 'o umlaut' }, { char: 'ø', name: 'o slash' },
            { char: 'œ', name: 'oe ligature' },
            { char: 'ú', name: 'u acute' }, { char: 'û', name: 'u circumflex' },
            { char: 'ü', name: 'u umlaut' }, { char: 'ß', name: 'sharp s eszett' },
            { char: '¿', name: 'inverted question mark' },
            { char: '¡', name: 'inverted exclamation mark' },
        ],
    },
    {
        id: 'greek',
        icon: 'α',
        chars: [
            { char: 'α', name: 'alpha' }, { char: 'β', name: 'beta' },
            { char: 'γ', name: 'gamma' }, { char: 'δ', name: 'delta' },
            { char: 'ε', name: 'epsilon' }, { char: 'θ', name: 'theta' },
            { char: 'λ', name: 'lambda' }, { char: 'μ', name: 'mu micro' },
            { char: 'π', name: 'pi' }, { char: 'ρ', name: 'rho' },
            { char: 'σ', name: 'sigma' }, { char: 'τ', name: 'tau' },
            { char: 'φ', name: 'phi' }, { char: 'χ', name: 'chi' },
            { char: 'ψ', name: 'psi' }, { char: 'ω', name: 'omega' },
            { char: 'Γ', name: 'capital gamma' }, { char: 'Δ', name: 'capital delta' },
            { char: 'Θ', name: 'capital theta' }, { char: 'Λ', name: 'capital lambda' },
            { char: 'Σ', name: 'capital sigma' }, { char: 'Φ', name: 'capital phi' },
            { char: 'Ω', name: 'capital omega ohm' },
        ],
    },
    {
        id: 'shapes',
        icon: '★',
        chars: [
            { char: '★', name: 'star filled' }, { char: '☆', name: 'star outline' },
            { char: '●', name: 'circle filled' }, { char: '○', name: 'circle outline' },
            { char: '■', name: 'square filled' }, { char: '□', name: 'square outline' },
            { char: '▲', name: 'triangle up' }, { char: '▼', name: 'triangle down' },
            { char: '◆', name: 'diamond' }, { char: '♦', name: 'diamond suit' },
            { char: '✓', name: 'check tick' }, { char: '✔', name: 'heavy check' },
            { char: '✗', name: 'cross ballot' }, { char: '✘', name: 'heavy cross' },
            { char: '☐', name: 'empty checkbox' }, { char: '☑', name: 'ticked checkbox' },
            { char: '♠', name: 'spade' }, { char: '♥', name: 'heart' },
            { char: '♣', name: 'club' }, { char: '☎', name: 'telephone' },
            { char: '✉', name: 'envelope mail' }, { char: '✂', name: 'scissors cut' },
        ],
    },
];

/** Every character whose name contains `query`, case-insensitively. */
export function searchSymbols(query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return [];
    return SYMBOL_GROUPS.flatMap((g) => g.chars).filter((c) => c.name.includes(q));
}
